import { ThemeProvider } from "@anchor/react-components/dist/lib/components/ThemeProvider";
import Toast from "@anchor/react-components/dist/lib/components/Toast";
import "../styles/globals.css";
import "../components/csop-tower/csop-tower.css";
import "../components/csop-tower/ReportingTab.css";
import "@maersk-global/fonts/maeu/fonts.css";
import "@maersk-global/mds-design-tokens/maersk/light/css/design-tokens-px.css";
import "@maersk-global/mds-foundations/css/foundations.css";
import type { AppProps, AppContext } from "next/app";
import type { NextPage } from "next";
import App from "next/app";
import AnchorAuthGuard from "@anchor/anchor-auth/AnchorAuthGuard";
import { McLoadingIndicator } from "@maersk-global/mds-react-wrapper/components-core/mc-loading-indicator";
import { enableJsonLogging } from "../utils/console-json";
import getConfig from "next/config";
import { createContext, useContext, useEffect } from "react";
import { initFaro } from "../config/faro";
import { useAnalyticsPageViews, useInteractionTracking } from "../config/analytics";
import type { ComponentProps, FC, ReactNode } from "react";
import {
  NotificationProvider as ToastNotificationProvider,
  NotificationInitializer,
} from "../components/Notification/index";
import { NotificationProvider } from "../components/Notification/NotificationProvider";

// Enable JSON logging if LOG_FORMAT=json
if (typeof window === "undefined") {
  enableJsonLogging();
}

// Create context for runtime config
export const RuntimeConfigContext = createContext<Record<string, any>>({});

// Hook to use runtime config
export const useRuntimeConfig = () => useContext(RuntimeConfigContext);

export type NextPageWithAuthentication = NextPage & {
  requiresAuthentication?: boolean;
};

type MyAppProps = Omit<AppProps, "Component"> & {
  Component: NextPageWithAuthentication;
  runtimeConfig: Record<string, any>;
};

// AnchorAuthGuard forwards a `skeleton` render prop at runtime, but its
// published types don't declare it yet — widen the props so we can pass ours.
const AuthGuard = AnchorAuthGuard as FC<
  ComponentProps<typeof AnchorAuthGuard> & { skeleton?: () => ReactNode }
>;

// Replaces AnchorAuthGuard's hardcoded "Authentication in progress..." text
// with an MDS spinner while the session is being established.
const AuthSkeleton = () => (
  <div
    style={{
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <McLoadingIndicator label="Loading" variant="ring" />
  </div>
);

function MyApp({ Component, pageProps, runtimeConfig }: MyAppProps) {
  const sessionRefetchInterval =
    Number(process.env.NEXT_PUBLIC_SESSION_REFETCH_INTERVAL) || 6 * 60 * 60;

  // Client-only: the Faro SDK and trace.initRootSpan() both touch `window`.
  // No-ops when RUM_API_KEY is absent (see config/faro.ts).
  useEffect(() => {
    initFaro();
  }, []);

  // Must run after initFaro(): page views emitted before Faro exists are lost.
  // Effects fire in declaration order, so keeping this second is the guarantee.
  useAnalyticsPageViews();

  // Delegated click capture — covers every module's controls without each one
  // having to be hand-instrumented. See useInteractionTracking for what it will
  // and will not report.
  useInteractionTracking();

  const page = <Component {...pageProps} />;
  const content =
    Component.requiresAuthentication === false ? (
      page
    ) : (
      <AuthGuard
        pageProps={pageProps}
        refetchInterval={sessionRefetchInterval}
        skeleton={AuthSkeleton}
      >
        <ToastNotificationProvider>
          <NotificationInitializer />
          <NotificationProvider>
            <Toast />
            {page}
          </NotificationProvider>
        </ToastNotificationProvider>
      </AuthGuard>
    );

  return (
    <RuntimeConfigContext.Provider value={runtimeConfig}>
      <ThemeProvider>{content}</ThemeProvider>
    </RuntimeConfigContext.Provider>
  );
}

MyApp.getInitialProps = async (appContext: AppContext) => {
  const appProps = await App.getInitialProps(appContext);
  const { publicRuntimeConfig } = getConfig();

  return {
    ...appProps,
    runtimeConfig: publicRuntimeConfig || {},
  };
};

export default MyApp;
